/** The small read-only lists of the agent package: inputs, model, tools, run settings. */

import type { ReactNode } from "react";
import { Box, BoxHeader } from "@/components/ui/box";
import { Pill } from "@/components/ui/pill";
import type { AgentSpec, InputField, RunSetting, Tool } from "./types";
import { SpecDescription } from "./spec-description";

export function Section({ title, count, children }: { title: string; count?: number; children: ReactNode }) {
  return (
    <Box label={title}>
      <BoxHeader title={title}>
        {count !== undefined && <span className="font-mono text-[11px] text-faint">{count}</span>}
      </BoxHeader>
      {children}
    </Box>
  );
}

function Row({ children }: { children: ReactNode }) {
  return (
    <li className="flex flex-col gap-1 border-b border-line-soft px-4 py-2.5 text-[13px] last:border-0">
      {children}
    </li>
  );
}

function None({ children }: { children: ReactNode }) {
  return <p className="px-4 py-3 text-[13px] text-muted">{children}</p>;
}

export function InputsList({ inputs }: { inputs: InputField[] }) {
  if (inputs.length === 0) return <None>This agent takes no named inputs.</None>;
  return (
    <ul aria-label="Inputs">
      {inputs.map((i) => (
        <Row key={i.name}>
          <span className="flex items-center gap-2">
            <span className="font-mono text-xs font-medium">{i.name}</span>
            <span className="flex-1" />
            <Pill>{i.kind}</Pill>
            <Pill tone={i.required ? "warn" : "neutral"}>{i.required ? "required" : "optional"}</Pill>
          </span>
          <SpecDescription text={i.description} source={i.description_source} />
        </Row>
      ))}
    </ul>
  );
}

const paramText = (v: unknown) => (typeof v === "string" ? v : JSON.stringify(v));

export function ModelCard({ spec }: { spec: AgentSpec }) {
  const params = Object.entries(spec.params);
  return (
    <div className="flex flex-col gap-1 px-4 py-3 text-[13px]">
      <span className="font-mono text-sm font-medium">{spec.model ?? "Not reported"}</span>
      {params.length > 0 && (
        <dl
          className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 pt-1.5 text-xs"
          aria-label="Model params"
        >
          {params.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-muted">{k}</dt>
              <dd className="m-0 truncate font-mono">{paramText(v)}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

export function ToolsList({ tools }: { tools: Tool[] }) {
  if (tools.length === 0) return <None>This agent uses no tools.</None>;
  return (
    <ul aria-label="Tools">
      {tools.map((t) => (
        <Row key={`${t.source}:${t.name}`}>
          <span className="flex items-center gap-2">
            <span className="font-mono text-xs font-medium">{t.name}</span>
            <span className="flex-1" />
            {t.source && <span className="font-mono text-[11px] text-faint">{t.source}</span>}
          </span>
          {t.description && <span className="text-xs text-muted">{t.description}</span>}
        </Row>
      ))}
    </ul>
  );
}

function settingValues(s: RunSetting): string {
  if (s.kind === "secret") return "Secret, picked from saved credentials";
  if (s.kind === "select") return s.options.map((o) => o.label || o.value).join(" / ");
  return s.default ? `Text · default ${s.default}` : "Text";
}

export function RunSettingsList({ settings }: { settings: RunSetting[] }) {
  if (settings.length === 0) return <None>Nothing to set per experiment.</None>;
  return (
    <ul aria-label="Run settings">
      {settings.map((s) => (
        <Row key={s.key}>
          <span className="flex items-center gap-2">
            <span className="font-medium">{s.label || s.key}</span>
            <span className="flex-1" />
            {s.required && <Pill tone="warn">required</Pill>}
          </span>
          <span className="text-xs text-muted">{settingValues(s)}</span>
          <SpecDescription text={s.description} source={s.description_source} />
        </Row>
      ))}
    </ul>
  );
}
